/**
 * @file components/workbench/AuditorWorkbench.tsx
 * @description Auditor（稽核員）角色工作台，接入真實後端資料。
 *
 * 信息架構（從上到下）：
 *   1. PageShell header — eyebrow + 大標問候 + 摘要 + meta（日期/在手案件）
 *   2. 3 張 StatCard — 進行中案件 / 待提交報告 / 緊急報告
 *   3. 我的進行中案件（左 2/3）+ 快速入口（右 1/3）
 *   4. 待提交報告 + 近期 NC 動態（雙欄）
 *
 * NOTE: 今日排程 與 本週工時 已移除——需後續引入排程表（scheduling table）
 *       與工時表（timesheet table）才有真實資料來源，屆時再補入 StatCard 與 SectionCard。
 */

import { Button, Empty, Spin } from 'antd';
import { useNavigate } from '@tanstack/react-router';
import { formatLongDate } from '@/lib/date-time';
import { useAuthStore } from '@/lib/auth-context';
import { PageShell, PageMetaItem, SectionCard, StatCard, StatusPill } from '@/components/shell';
import { color, font, leading, radius, roleAccent, space, text, weight } from '@/styles/tokens';
import {
  Activity,
  AlarmClock,
  ArrowRight,
  Briefcase,
  CalendarDays,
  Camera,
  CheckCircle2,
  ChevronRight,
  Clock3,
  CloudUpload,
  FileBarChart,
  FileSignature,
  Hourglass,
  Inbox,
  MapPin,
  Scale,
  Sparkles,
  TriangleAlert,
  WifiOff,
} from 'lucide-react';
import { useAuditorDashboard } from '@/hooks/useWorkbench';
import { userCan } from '@zhcb/shared';
import type { AuditorDashboardData } from '@zhcb/shared';

// ─── 輔助函數 ─────────────────────────────────────────────────────────────────

/**
 * 將 DB 原始 severity 字串映射到顯示標籤與色調。
 * 已知值：critical / major / minor；未知值 fallback 到 info。
 */
function severityDisplay(severity: string): {
  label: string;
  tone: 'danger' | 'warning' | 'info';
} {
  switch (severity) {
    case 'critical':
      return { label: '重大', tone: 'danger' };
    case 'major':
      return { label: '主要', tone: 'warning' };
    case 'minor':
      return { label: '輕微', tone: 'info' };
    default:
      return { label: severity, tone: 'info' };
  }
}

/** 將 workflowState 對應到 StatusPill tone */
function workflowTone(state: string): 'warning' | 'brand' | 'info' | 'danger' | 'success' {
  switch (state) {
    case '稽核中':
      return 'warning';
    case '報告中':
    case '報告評審':
      return 'brand';
    case '已繳費':
      return 'success';
    case '已派案':
    default:
      return 'info';
  }
}

// ─── 類型別名 ─────────────────────────────────────────────────────────────────

type ActiveCaseItem = AuditorDashboardData['activeCases'][number];
type PendingReportItem = AuditorDashboardData['pendingReports'][number];
type RecentNCItem = AuditorDashboardData['recentNC'][number];
type PendingDecisionItem = AuditorDashboardData['pendingDecisions'][number];

// ─── 子組件 ────────────────────────────────────────────────────────────────────

/**
 * 我的進行中案件卡片行（真實資料版）。
 * 顯示：案件編號、客戶名稱、工作流狀態、截止日距今天數。
 */
function CaseRow({ item, onClick }: { item: ActiveCaseItem; onClick: () => void }) {
  const daysLeft = item.daysLeft;
  const dueColor =
    daysLeft === null
      ? color.text.tertiary
      : daysLeft < 0
        ? color.state.danger
        : daysLeft <= 3
          ? color.state.danger
          : daysLeft <= 7
            ? color.state.warning
            : color.text.tertiary;
  const tone = workflowTone(item.workflowState);

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        all: 'unset',
        display: 'block',
        width: '100%',
        padding: `${space[4]}px ${space[5]}px`,
        cursor: 'pointer',
        transition: 'background 120ms cubic-bezier(0.16, 1, 0.3, 1)',
        borderBottom: `1px solid ${color.border.subtle}`,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = color.surface.muted;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
      }}
    >
      {/* 案件編號 + 客戶 + 工作流狀態 + 截止日 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: space[3],
          flexWrap: 'wrap',
        }}
      >
        <span
          style={{
            fontFamily: font.mono,
            fontSize: text.md,
            fontWeight: weight.semibold,
            color: color.brand.primary,
          }}
        >
          {item.caseCode}
        </span>
        <span
          style={{
            fontSize: text.md,
            fontWeight: weight.semibold,
            color: color.text.primary,
          }}
        >
          {item.clientName ?? '—'}
        </span>
        <StatusPill tone={tone} size="sm">
          {item.workflowState}
        </StatusPill>
        <span style={{ flex: 1 }} />
        {daysLeft !== null && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              fontSize: text.sm,
              color: dueColor,
              fontWeight: weight.semibold,
              whiteSpace: 'nowrap',
            }}
          >
            <Hourglass size={12} />
            {daysLeft < 0 ? `逾期 ${Math.abs(daysLeft)} 日` : `${daysLeft} 日`}
            {item.dueDate && (
              <span style={{ fontFamily: font.mono, marginLeft: 4 }}>
                · {item.dueDate.slice(0, 10)}
              </span>
            )}
          </span>
        )}
      </div>
    </button>
  );
}

/**
 * 待辦事項分組小標題（稽核任務 / 審議任務）：僅在兩類任務同時存在時渲染，以資區分。
 */
function TodoGroupHeader({ label, count }: { label: string; count: number }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: space[2],
        padding: `${space[2]}px ${space[5]}px`,
        background: color.surface.muted,
        borderBottom: `1px solid ${color.border.subtle}`,
        fontSize: text.xs,
        fontWeight: weight.semibold,
        color: color.text.tertiary,
        letterSpacing: 0.4,
      }}
    >
      {label}
      <span style={{ color: color.text.muted, fontWeight: weight.medium }}>({count})</span>
    </div>
  );
}

/**
 * 審議任務行（稽核員兼驗證決定委員時）：待本人簽署的審議中決定。
 * 需求（xlsx(2) 任務4）：工作台「我的待辦事項」須同時涵蓋稽核任務 + 審議任務。
 * 點擊跳 /decisions/$id 進入書面審議。
 */
function DecisionRow({ item, onClick }: { item: PendingDecisionItem; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        all: 'unset',
        display: 'block',
        width: '100%',
        padding: `${space[4]}px ${space[5]}px`,
        cursor: 'pointer',
        transition: 'background 120ms cubic-bezier(0.16, 1, 0.3, 1)',
        borderBottom: `1px solid ${color.border.subtle}`,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = color.surface.muted;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: space[3], flexWrap: 'wrap' }}>
        {/* 決定編號（審議任務標識） */}
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontFamily: font.mono,
            fontSize: text.md,
            fontWeight: weight.semibold,
            color: color.state.warning,
          }}
        >
          <Scale size={13} />
          {item.code}
        </span>
        <span style={{ fontSize: text.md, fontWeight: weight.semibold, color: color.text.primary }}>
          {item.clientName ?? '—'}
        </span>
        {/* 審議任務標籤，與稽核案件的工作流狀態 pill 區分 */}
        <StatusPill tone="warning" size="sm">
          待我簽
        </StatusPill>
        {item.reviewMethod && (
          <StatusPill tone="info" size="sm">
            {item.reviewMethod}
          </StatusPill>
        )}
        <span style={{ flex: 1 }} />
        {(item.scheduledAt ?? item.dueAt) && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              fontSize: text.sm,
              color: color.text.tertiary,
              fontWeight: weight.medium,
              whiteSpace: 'nowrap',
            }}
          >
            <CalendarDays size={12} />
            {(item.scheduledAt ?? item.dueAt)!.slice(0, 10)}
          </span>
        )}
      </div>
    </button>
  );
}

/**
 * 待提交報告行（真實資料版）。
 */
function ReportRow({ item }: { item: PendingReportItem }) {
  const isUnderReview = item.status === '評審中';
  const stateTone = isUnderReview ? ('warning' as const) : ('info' as const);
  const stateLabel = isUnderReview ? '評審中' : '草稿';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: space[3],
        padding: `${space[3]}px ${space[5]}px`,
        borderBottom: `1px solid ${color.border.subtle}`,
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: radius.md,
          background: isUnderReview ? color.state.warningSoft : color.brand.primarySoft,
          color: isUnderReview ? color.state.warning : color.brand.primary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <FileSignature size={16} strokeWidth={2.2} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: space[2],
            marginBottom: 3,
            flexWrap: 'wrap',
          }}
        >
          <span
            style={{
              fontFamily: font.mono,
              fontSize: text.sm,
              color: color.brand.primary,
              fontWeight: weight.semibold,
            }}
          >
            {item.code}
          </span>
          <span
            style={{
              fontSize: text.md,
              fontWeight: weight.semibold,
              color: color.text.primary,
            }}
          >
            {item.clientName ?? '—'}
          </span>
          <StatusPill tone={stateTone} size="sm">
            {stateLabel}
          </StatusPill>
        </div>
        <div style={{ fontSize: text.sm, color: color.text.tertiary }}>{item.status}</div>
      </div>
      <div
        style={{
          fontSize: text.sm,
          color: item.urgent ? color.state.danger : color.text.tertiary,
          fontWeight: item.urgent ? weight.semibold : weight.medium,
          whiteSpace: 'nowrap',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        <AlarmClock size={13} />
        {item.dueIn}
      </div>
    </div>
  );
}

/**
 * NC 動態行（真實資料版）。
 * severity 為 DB 原始字串，透過 severityDisplay() 映射到顯示標籤與色調。
 */
function NCRow({ item }: { item: RecentNCItem }) {
  const { label: severityLabel, tone: severityTone } = severityDisplay(item.severity);

  const statusIcon =
    item.status === 'overdue' ? (
      <TriangleAlert size={14} />
    ) : item.status === 'submitted' ? (
      <CheckCircle2 size={14} />
    ) : (
      <Clock3 size={14} />
    );
  const statusLabel =
    item.status === 'overdue'
      ? '已逾期'
      : item.status === 'submitted'
        ? '已收改善'
        : item.status === 'closed'
          ? '已關閉'
          : '待業主回覆';
  const statusColor =
    item.status === 'overdue'
      ? color.state.danger
      : item.status === 'submitted'
        ? color.state.success
        : color.text.tertiary;

  return (
    <div
      style={{
        display: 'flex',
        gap: space[3],
        padding: `${space[3]}px ${space[5]}px`,
        borderBottom: `1px solid ${color.border.subtle}`,
        alignItems: 'flex-start',
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: radius.md,
          background:
            severityTone === 'danger'
              ? color.state.dangerSoft
              : severityTone === 'warning'
                ? color.state.warningSoft
                : color.state.infoSoft,
          color:
            severityTone === 'danger'
              ? color.state.danger
              : severityTone === 'warning'
                ? color.state.warning
                : color.state.info,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          marginTop: 2,
        }}
      >
        <TriangleAlert size={16} strokeWidth={2.2} />
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: space[2],
            marginBottom: 3,
            flexWrap: 'wrap',
          }}
        >
          <span
            style={{
              fontFamily: font.mono,
              fontSize: text.sm,
              color: color.brand.primary,
              fontWeight: weight.semibold,
            }}
          >
            {item.code}
          </span>
          <StatusPill tone={severityTone} size="sm">
            {severityLabel}
          </StatusPill>
        </div>
        <div
          style={{
            fontSize: text.md,
            fontWeight: weight.medium,
            color: color.text.primary,
            marginBottom: 3,
            lineHeight: leading.snug,
          }}
        >
          {item.description}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: space[3],
            fontSize: text.sm,
            color: color.text.tertiary,
            flexWrap: 'wrap',
          }}
        >
          {item.dueDate && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <CalendarDays size={12} />
              截止 {item.dueDate.slice(0, 10)}
            </span>
          )}
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              color: statusColor,
              fontWeight: weight.semibold,
            }}
          >
            {statusIcon}
            {statusLabel}
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * 快速入口大按鈕。
 */
interface QuickActionProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  badge?: string;
  badgeTone?: 'brand' | 'warning' | 'success';
  onClick: () => void;
  highlighted?: boolean;
}

function QuickAction({
  icon,
  title,
  description,
  badge,
  badgeTone = 'brand',
  onClick,
  highlighted,
}: QuickActionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        all: 'unset',
        display: 'flex',
        alignItems: 'center',
        gap: space[3],
        padding: space[4],
        background: highlighted ? color.brand.primarySoft : color.surface.base,
        border: `1px solid ${highlighted ? 'rgba(79, 70, 229, 0.20)' : color.border.subtle}`,
        borderRadius: radius.md,
        cursor: 'pointer',
        transition: 'all 200ms cubic-bezier(0.16, 1, 0.3, 1)',
        width: '100%',
        boxSizing: 'border-box',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = highlighted
          ? color.brand.primary
          : color.border.default;
        e.currentTarget.style.transform = 'translateY(-1px)';
        e.currentTarget.style.boxShadow =
          '0 2px 4px rgba(15, 23, 42, 0.04), 0 6px 12px rgba(15, 23, 42, 0.05)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = highlighted
          ? 'rgba(79, 70, 229, 0.20)'
          : color.border.subtle;
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <span
        style={{
          width: 40,
          height: 40,
          borderRadius: radius.md,
          background: highlighted ? roleAccent.auditor.gradient : color.brand.primarySoft,
          color: highlighted ? '#fff' : color.brand.primary,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: space[2], marginBottom: 2 }}>
          <span
            style={{
              fontSize: text.md,
              fontWeight: weight.semibold,
              color: color.text.primary,
            }}
          >
            {title}
          </span>
          {badge && (
            <StatusPill tone={badgeTone} size="sm" dot>
              {badge}
            </StatusPill>
          )}
        </div>
        <div
          style={{
            fontSize: text.sm,
            color: color.text.tertiary,
            lineHeight: leading.snug,
          }}
        >
          {description}
        </div>
      </div>
      <ChevronRight size={16} color={color.text.muted} />
    </button>
  );
}

// ─── 主組件 ────────────────────────────────────────────────────────────────────

/**
 * Auditor 工作台組件。
 * 顯示進行中案件（行級過濾）、待提交報告、NC 動態，資料來源為 /api/dashboard/auditor/workbench。
 */
export function AuditorWorkbench() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user!);
  const { data: d, isLoading } = useAuditorDashboard();

  /* 多角色：稽核員兼驗證決定委員（capabilities 有 decision.vote 或 roles 含 decider）時，快速入口顯示「我的審議任務」 */
  const canVoteDecision = userCan(user, 'decision.vote');

  /* 問候語：依時段切換 */
  const hour = new Date().getHours();
  const greeting = hour < 6 ? '深夜好' : hour < 12 ? '早安' : hour < 18 ? '午安' : '晚安';

  const activeCaseCount = d?.stats.activeCases ?? 0;
  const pendingReportCount = d?.stats.pendingReports ?? 0;
  const urgentReportCount = d?.stats.urgentReports ?? 0;
  /* 審議任務（稽核員兼委員時的待簽審議中決定，xlsx(2) 任務4） */
  const pendingDecisions = d?.pendingDecisions ?? [];
  const pendingDecisionCount = pendingDecisions.length;

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <PageShell
      title={
        <span>
          {greeting}，{user.name}
        </span>
      }
      description={
        <>
          目前有 <strong style={{ color: color.text.primary }}>{activeCaseCount}</strong>{' '}
          件進行中案件，
          <strong style={{ color: color.state.danger }}>{urgentReportCount}</strong>{' '}
          份報告需儘快提交。實地稽核別忘了帶離線版檢查表。
        </>
      }
      meta={
        <>
          <PageMetaItem
            icon={<CalendarDays size={13} />}
            label="日期"
            value={formatLongDate(new Date())}
          />
          <PageMetaItem
            icon={<Briefcase size={13} />}
            label="在手案件"
            value={`${activeCaseCount} 件`}
          />
        </>
      }
      actions={
        <Button type="primary" icon={<CloudUpload size={14} />}>
          上傳報告
        </Button>
      }
    >
      {/* ═══ 第一排：3 張統計卡 ═══ */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: space[4],
          marginBottom: space[6],
        }}
      >
        <StatCard
          title="進行中案件"
          value={activeCaseCount}
          suffix="件"
          tone="warning"
          icon={<Briefcase size={20} />}
          onClick={() => void navigate({ to: '/audit-tasks' })}
        />
        <StatCard
          title="待提交報告"
          value={pendingReportCount}
          suffix="份"
          tone="info"
          icon={<FileSignature size={20} />}
        />
        <StatCard
          title="緊急報告"
          value={urgentReportCount}
          suffix="份"
          tone="danger"
          icon={<TriangleAlert size={20} />}
        />
      </div>

      {/* ═══ 第二排：我的進行中案件（左 2/3）+ 快速入口（右 1/3） ═══ */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)',
          gap: space[4],
          marginBottom: space[6],
        }}
      >
        <SectionCard
          icon={<Briefcase size={14} />}
          title="我的待辦事項"
          // xlsx(2) 任務4：稽核員工作台待辦同時涵蓋稽核任務 + 審議任務（兼委員時）
          description="稽核任務 + 審議任務，按截止日距離排序"
          actions={
            <Button
              type="text"
              size="small"
              icon={<ArrowRight size={13} />}
              onClick={() => void navigate({ to: '/audit-tasks' })}
            >
              全部 ({activeCaseCount})
            </Button>
          }
          padless
        >
          {!activeCaseCount && !pendingDecisionCount ? (
            <div style={{ padding: `${space[8]}px 0` }}>
              <Empty description="目前無待辦事項" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </div>
          ) : (
            <>
              {/* 稽核任務（進行中案件）；僅在同時有審議任務時顯示分組小標題 */}
              {activeCaseCount > 0 && (
                <>
                  {pendingDecisionCount > 0 && (
                    <TodoGroupHeader label="稽核任務" count={activeCaseCount} />
                  )}
                  {d!.activeCases.map((item) => (
                    <CaseRow
                      key={`case-${item.id}`}
                      item={item}
                      // 序167.2 / 2026-09-13 回報序17：點擊待辦案件直達「稽/審核流程管理」任務詳情
                      // （/audit-tasks/:taskId 第 1 階段含派案確認）。舊版誤跳 /dispatch/:caseId
                      // 派案專區（舊的確認派案模式）。無任務（理論上不會發生）時回落任務列表。
                      onClick={() =>
                        item.taskId
                          ? void navigate({
                              to: '/audit-tasks/$taskId',
                              params: { taskId: String(item.taskId) },
                              search: { phase: 'preparation' },
                            })
                          : void navigate({ to: '/audit-tasks' })
                      }
                    />
                  ))}
                </>
              )}
              {/* 審議任務（待本人簽署的審議中決定），點擊跳決定詳情進入書面審議 */}
              {pendingDecisionCount > 0 && (
                <>
                  {activeCaseCount > 0 && (
                    <TodoGroupHeader label="審議任務" count={pendingDecisionCount} />
                  )}
                  {pendingDecisions.map((item) => (
                    <DecisionRow
                      key={`decision-${item.id}`}
                      item={item}
                      onClick={() =>
                        void navigate({ to: '/decisions/$id', params: { id: String(item.id) } })
                      }
                    />
                  ))}
                </>
              )}
            </>
          )}
        </SectionCard>

        <SectionCard icon={<Sparkles size={14} />} title="快速入口" description="現場常用工具">
          <div style={{ display: 'flex', flexDirection: 'column', gap: space[3] }}>
            <QuickAction
              icon={<MapPin size={18} strokeWidth={2.2} />}
              title="現場工作臺"
              description="檢查表 / 打卡 / GPS 標記"
              onClick={() => void navigate({ to: '/audit-tasks' })}
              highlighted
            />
            <QuickAction
              icon={<Camera size={18} strokeWidth={2.2} />}
              title="上傳證據"
              description="現場照片、文件、抽樣紀錄"
              onClick={() => void navigate({ to: '/audit-tasks' })}
            />
            <QuickAction
              icon={<WifiOff size={18} strokeWidth={2.2} />}
              title="離線同步"
              description="本機資料待回傳伺服器"
              onClick={() => void navigate({ to: '/me/offline' })}
            />
            <QuickAction
              icon={<Inbox size={18} strokeWidth={2.2} />}
              title="收件匣"
              description="新派案件 + 隊長批示"
              onClick={() => void navigate({ to: '/audit-tasks' })}
            />
            {/* 稽核員兼驗證決定委員：直達審議列表（本人稽核的案件由後端 COI 迴避） */}
            {canVoteDecision && (
              <QuickAction
                icon={<Scale size={18} strokeWidth={2.2} />}
                title="審議任務"
                description="驗證決定待我簽署"
                onClick={() => void navigate({ to: '/decisions' })}
              />
            )}
          </div>
        </SectionCard>
      </div>

      {/* ═══ 第三排：待提交報告 + NC 動態（雙欄） ═══ */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
          gap: space[4],
        }}
      >
        <SectionCard
          icon={<FileBarChart size={14} />}
          title="待提交報告"
          description="草稿與評審中報告"
          actions={
            <Button type="text" size="small">
              全部 ({pendingReportCount})
            </Button>
          }
          padless
        >
          {!d?.pendingReports?.length ? (
            <div style={{ padding: `${space[8]}px 0` }}>
              <Empty description="目前無待提交報告" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </div>
          ) : (
            d.pendingReports.map((item) => <ReportRow key={item.id} item={item} />)
          )}
        </SectionCard>

        <SectionCard
          icon={<Activity size={14} />}
          title="近期 NC 動態"
          description="我負責驗收的不符合事項"
          padless
        >
          {!d?.recentNC?.length ? (
            <div style={{ padding: `${space[8]}px 0` }}>
              <Empty description="目前無近期 NC" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </div>
          ) : (
            d.recentNC.map((item) => <NCRow key={item.code} item={item} />)
          )}
        </SectionCard>
      </div>
    </PageShell>
  );
}
